/**
 * MASTER 04 — MACRO_SENSORY / «Макро · сенсорные ноты».
 *
 * One strong macro photograph paired with concise author observations about
 * texture, aroma, taste, appearance or finish. Fully generic and reusable
 * across every Digital Editions surface — paid Volumes (Almond pilot, future
 * volumes), Open Library, paid courses, Gingerbread Workshop and future
 * editorial collections. NOTHING here is Almond/Tome-I/product/route/access
 * specific.
 *
 * Storage:
 *   All structured data lives inside `block.content.macro_sensory`
 *   (the backend `BlockContent` schema exposes `macro_sensory: Optional[Dict[str, Any]]`).
 *   Every unknown or legacy top-level key of `block.content` is preserved
 *   verbatim on save.
 *
 * Constraints:
 *   – At most 6 sensory notes (author-defined labels).
 *   – Stable IDs for both text blocks and sensory notes.
 *   – Mobile image is optional and gracefully falls back to the desktop
 *     image on mobile preview; empty optional fields render nothing.
 *
 * Shared primitives reused from HeroMaster / CropModal — no second media
 * workflow is created here.
 */
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  ArrowUp, ArrowDown, Trash2, Plus, Aperture,
} from "lucide-react";
import CropModal from "@/components/editorial/CropModal";
import {
  ImageSlot,
  MediaLibraryPicker,
} from "@/components/editorial/HeroMaster";
import { resolveMediaUrl } from "@/lib/editorialMedia.mjs";
import { editorialImageStyle, hasExplicitEditorialCrop } from "@/lib/editorialReaderLayout.mjs";

const MAX_SENSORY_NOTES = 6;

const genId = (p = "id") =>
  `${p}${Date.now().toString(36)}${Math.floor(Math.random() * 1e4)}`;

const emptyImage = () => ({
  media_id: null, url: null, alt: "", focal_x: 0.5, focal_y: 0.5, zoom: 1,
});

function readMacro(rawContent) {
  const c = rawContent || {};
  const m = (c.macro_sensory && typeof c.macro_sensory === "object")
    ? c.macro_sensory : {};

  const readImg = (img) => ({
    media_id: img?.media_id ?? null,
    url:      img?.url      ?? null,
    alt:      img?.alt      ?? "",
    focal_x:  img?.focal_x  ?? 0.5,
    focal_y:  img?.focal_y  ?? 0.5,
    zoom:     img?.zoom     ?? 1,
  });

  const blocks = Array.isArray(m.text_blocks) && m.text_blocks.length
    ? m.text_blocks.map((b) => ({ id: b.id || genId("mp"), text: b.text || "" }))
    : [{ id: genId("mp"), text: "" }];

  const notes = Array.isArray(m.sensory_notes)
    ? m.sensory_notes.slice(0, MAX_SENSORY_NOTES).map((n) => ({
        id:    n.id    || genId("sn"),
        label: n.label || "",
        text:  n.text  || "",
      }))
    : [];

  return {
    eyebrow:      m.eyebrow      ?? "",
    title:        m.title        ?? c.headline ?? "",
    subtitle:     m.subtitle     ?? c.lead     ?? "",
    text_blocks:  blocks,
    sensory_notes: notes,
    accent_phrase: m.accent_phrase ?? "",
    caption:       m.caption       ?? c.caption ?? "",
    main_image:    readImg(m.main_image),
    mobile_image:  readImg(m.mobile_image),
  };
}

// -------------------- Editor --------------------

export function MacroSensoryEditor({ block, onPatch }) {
  const m = useMemo(() => readMacro(block.content), [block.content]);
  const patch = (delta) => onPatch({
    content: { ...(block.content || {}), macro_sensory: { ...m, ...delta } },
  });

  const [mainLibraryOpen, setMainLibraryOpen] = useState(false);
  const [mobileLibraryOpen, setMobileLibraryOpen] = useState(false);
  const [mainCropOpen, setMainCropOpen]     = useState(false);
  const [mobileCropOpen, setMobileCropOpen] = useState(false);

  const setMain   = (delta) => patch({ main_image:   { ...m.main_image,   ...delta } });
  const setMobile = (delta) => patch({ mobile_image: { ...m.mobile_image, ...delta } });

  // -- ordered text blocks --
  const setBlocks = (blocks) => patch({ text_blocks: blocks });
  const addBlock = () => setBlocks([...m.text_blocks, { id: genId("mp"), text: "" }]);
  const removeBlock = (i) => {
    const next = [...m.text_blocks]; next.splice(i, 1);
    setBlocks(next.length ? next : [{ id: genId("mp"), text: "" }]);
  };
  const moveBlock = (i, dir) => {
    const next = [...m.text_blocks]; const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setBlocks(next);
  };
  const patchBlock = (i, delta) => {
    const next = [...m.text_blocks]; next[i] = { ...next[i], ...delta };
    setBlocks(next);
  };

  // -- sensory notes (max 6) --
  const setNotes = (notes) => patch({ sensory_notes: notes });
  const canAddNote = m.sensory_notes.length < MAX_SENSORY_NOTES;
  const addNote = () => {
    if (!canAddNote) return;
    setNotes([...m.sensory_notes, { id: genId("sn"), label: "", text: "" }]);
  };
  const removeNote = (i) => {
    const next = [...m.sensory_notes]; next.splice(i, 1); setNotes(next);
  };
  const moveNote = (i, dir) => {
    const next = [...m.sensory_notes]; const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setNotes(next);
  };
  const patchNote = (i, delta) => {
    const next = [...m.sensory_notes]; next[i] = { ...next[i], ...delta };
    setNotes(next);
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4"
         data-testid="macro-editor">
      {/* LEFT — text + sensory notes */}
      <div className="rounded-2xl bg-white border hairline p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Макро · сенсорные ноты · текст</div>

        <div>
          <label className="block text-[11px] opacity-60 mb-1">Метка раздела · необязательно</label>
          <Input value={m.eyebrow}
                 onChange={(e) => patch({ eyebrow: e.target.value })}
                 placeholder="МАКРО · ФАКТУРА"
                 data-testid="macro-field-eyebrow" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Заголовок</label>
          <Input value={m.title}
                 onChange={(e) => patch({ title: e.target.value })}
                 className="font-serif text-lg"
                 data-testid="macro-field-title" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Подзаголовок · лид · необязательно</label>
          <textarea rows={2} value={m.subtitle}
                    onChange={(e) => patch({ subtitle: e.target.value })}
                    className="w-full border rounded px-2 py-1.5 text-sm italic font-serif"
                    data-testid="macro-field-subtitle" />
        </div>

        <div className="pt-2">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest opacity-70">Абзацы</div>
            <button type="button" onClick={addBlock}
              className="text-[12px] inline-flex items-center gap-1 opacity-80 hover:opacity-100"
              data-testid="macro-add-block">
              <Plus className="w-3.5 h-3.5" /> Добавить абзац
            </button>
          </div>
          <div className="space-y-2">
            {m.text_blocks.map((b, i) => (
              <div key={b.id} className="rounded-lg border hairline bg-cream/40 p-3 space-y-2"
                   data-testid={`macro-block-${i}`}>
                <div className="flex items-center gap-2">
                  <div className="text-[10px] uppercase tracking-widest opacity-60 shrink-0 font-mono">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="flex-1" />
                  <button type="button" onClick={() => moveBlock(i, -1)} disabled={i === 0}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Выше" data-testid={`macro-block-up-${i}`}>
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => moveBlock(i, +1)} disabled={i === m.text_blocks.length - 1}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Ниже" data-testid={`macro-block-down-${i}`}>
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => removeBlock(i)}
                    className="p-1 opacity-60 hover:opacity-100 text-red-600"
                    title="Удалить" data-testid={`macro-block-remove-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <textarea rows={3} value={b.text}
                          onChange={(e) => patchBlock(i, { text: e.target.value })}
                          className="w-full border rounded px-2 py-1.5 text-sm font-serif leading-relaxed"
                          placeholder="Абзац наблюдений автора…"
                          data-testid={`macro-block-text-${i}`} />
              </div>
            ))}
          </div>
        </div>

        <div className="pt-2 border-t border-espresso/10">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest opacity-70">
              Сенсорные ноты · до {MAX_SENSORY_NOTES}
            </div>
            <button type="button" onClick={addNote} disabled={!canAddNote}
              className="text-[12px] inline-flex items-center gap-1 opacity-80 hover:opacity-100 disabled:opacity-25"
              data-testid="macro-add-note">
              <Plus className="w-3.5 h-3.5" /> Добавить ноту
            </button>
          </div>
          {m.sensory_notes.length === 0 ? (
            <div className="rounded-lg bg-cream/60 border border-dashed border-espresso/25 p-4 text-center">
              <div className="text-[12px] opacity-70">
                Ноты появятся в предпросмотре в виде тонкого «руф-листа» под текстом.
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {m.sensory_notes.map((n, i) => (
                <div key={n.id} className="rounded-lg border hairline bg-cream/40 p-3"
                     data-testid={`macro-note-${i}`}>
                  <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto] gap-2 items-center">
                    <Input value={n.label}
                           onChange={(e) => patchNote(i, { label: e.target.value })}
                           placeholder="Метка · например «Фактура»"
                           className="font-serif"
                           data-testid={`macro-note-label-${i}`} />
                    <Input value={n.text}
                           onChange={(e) => patchNote(i, { text: e.target.value })}
                           placeholder="Наблюдение автора"
                           className="font-serif"
                           data-testid={`macro-note-text-${i}`} />
                    <div className="flex items-center gap-1 shrink-0">
                      <button type="button" onClick={() => moveNote(i, -1)} disabled={i === 0}
                        className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                        title="Выше" data-testid={`macro-note-up-${i}`}>
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => moveNote(i, +1)} disabled={i === m.sensory_notes.length - 1}
                        className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                        title="Ниже" data-testid={`macro-note-down-${i}`}>
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => removeNote(i)}
                        className="p-1 opacity-60 hover:opacity-100 text-red-600"
                        title="Удалить" data-testid={`macro-note-remove-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pt-2 border-t border-espresso/10">
          <label className="block text-[11px] opacity-60 mb-1">Акцентная фраза · необязательно</label>
          <Input value={m.accent_phrase}
                 onChange={(e) => patch({ accent_phrase: e.target.value })}
                 placeholder="Одна фраза, задающая общее впечатление"
                 className="font-serif italic"
                 data-testid="macro-field-accent" />
        </div>
      </div>

      {/* RIGHT — images + caption */}
      <div className="rounded-2xl bg-white border hairline p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Макро-изображения</div>

        <div>
          <div className="text-[11px] opacity-60 mb-2">Основное макро · для компьютера</div>
          <ImageSlot
            label=""
            url={m.main_image.url}
            focalX={m.main_image.focal_x} focalY={m.main_image.focal_y} zoom={m.main_image.zoom}
            onFile={(x) => setMain({ media_id: x.id, url: x.file_url })}
            onClear={() => setMain({ media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1 })}
            onOpenLibrary={() => setMainLibraryOpen(true)}
            onOpenCrop={() => setMainCropOpen(true)}
            testid="macro-main-image" />
          <div className="mt-2">
            <Input placeholder="Описание изображения · необязательно"
                   value={m.main_image.alt}
                   onChange={(e) => setMain({ alt: e.target.value })}
                   data-testid="macro-main-alt" />
          </div>
        </div>

        <div className="pt-2 border-t border-espresso/10">
          <div className="text-[11px] opacity-60 mb-2">
            Мобильное макро · необязательно
            <span className="ml-1 opacity-70">(если пусто — используется основное)</span>
          </div>
          <ImageSlot
            label=""
            url={m.mobile_image.url}
            focalX={m.mobile_image.focal_x} focalY={m.mobile_image.focal_y} zoom={m.mobile_image.zoom}
            onFile={(x) => setMobile({ media_id: x.id, url: x.file_url })}
            onClear={() => setMobile({ media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1 })}
            onOpenLibrary={() => setMobileLibraryOpen(true)}
            onOpenCrop={() => setMobileCropOpen(true)}
            testid="macro-mobile-image" />
          <div className="mt-2">
            <Input placeholder="Описание изображения · необязательно"
                   value={m.mobile_image.alt}
                   onChange={(e) => setMobile({ alt: e.target.value })}
                   data-testid="macro-mobile-alt" />
          </div>
        </div>

        <div className="pt-2 border-t border-espresso/10">
          <label className="block text-[11px] opacity-60 mb-1">Подпись под изображением · необязательно</label>
          <Input value={m.caption}
                 onChange={(e) => patch({ caption: e.target.value })}
                 placeholder="Короткая подпись автора"
                 data-testid="macro-field-caption" />
        </div>
      </div>

      <MediaLibraryPicker
        open={mainLibraryOpen}
        onClose={() => setMainLibraryOpen(false)}
        onPick={(x) => { setMain({ media_id: x.id, url: x.file_url || x.url }); setMainLibraryOpen(false); }} />
      <MediaLibraryPicker
        open={mobileLibraryOpen}
        onClose={() => setMobileLibraryOpen(false)}
        onPick={(x) => { setMobile({ media_id: x.id, url: x.file_url || x.url }); setMobileLibraryOpen(false); }} />
      <CropModal
        open={mainCropOpen}
        url={m.main_image.url}
        focalX={m.main_image.focal_x} focalY={m.main_image.focal_y} zoom={m.main_image.zoom}
        onClose={() => setMainCropOpen(false)}
        onSave={({ focal_x, focal_y, zoom }) => { setMain({ focal_x, focal_y, zoom }); setMainCropOpen(false); }} />
      <CropModal
        open={mobileCropOpen}
        url={m.mobile_image.url}
        focalX={m.mobile_image.focal_x} focalY={m.mobile_image.focal_y} zoom={m.mobile_image.zoom}
        onClose={() => setMobileCropOpen(false)}
        onSave={({ focal_x, focal_y, zoom }) => { setMobile({ focal_x, focal_y, zoom }); setMobileCropOpen(false); }} />
    </div>
  );
}

// -------------------- Reader render --------------------

function MacroImage({ img, mode = "desktop" }) {
  if (!img?.url) return null;
  const hasCrop = hasExplicitEditorialCrop(img);
  const maxHeightClass = mode === "mobile" ? "max-h-[300px]" : "max-h-[520px]";
  return (
    <div className={`flex w-full justify-center overflow-hidden ${hasCrop ? "aspect-[5/4] bg-neutral-100" : ""}`}
         data-testid="macro-image">
      <img
        src={resolveMediaUrl(img.url)}
        alt={img.alt || ""}
        className={hasCrop ? "h-full w-full" : `block h-auto w-auto max-w-full ${maxHeightClass}`}
        style={editorialImageStyle(img)}
      />
    </div>
  );
}

function SensoryList({ notes }) {
  if (!notes.length) return null;
  return (
    <ul className="divide-y divide-espresso/15" data-testid="macro-sensory-list">
      {notes.map((n) => {
        if (!n.label && !n.text) return null;
        return (
          <li key={n.id}
              className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] items-baseline gap-5 py-2.5"
              data-testid={`macro-note-${n.id}`}>
            <div className="text-[10px] tracking-[0.24em] uppercase" style={{ color: "#8A6A46" }}>
              {n.label}
            </div>
            <div className="font-serif text-[14px] leading-[1.6]" style={{ color: "#2A1D14" }}>
              {n.text}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function MacroSensoryRender({ block, mode = "desktop" }) {
  const m = useMemo(() => readMacro(block.content), [block.content]);
  const isMobile = mode === "mobile";

  const activeImage = isMobile
    ? (m.mobile_image.url ? m.mobile_image : m.main_image)
    : m.main_image;
  const hasImage = !!(activeImage && activeImage.url);

  const paragraphs = (m.text_blocks || []).filter((b) => b.text && b.text.trim());
  const notes = (m.sensory_notes || []).filter((n) => (n.label && n.label.trim()) || (n.text && n.text.trim()));

  const Eyebrow = m.eyebrow ? (
    <div className="inline-flex items-center gap-2 mb-4 text-[10px] tracking-[0.28em] uppercase"
         style={{ color: "#8A6A46" }}
         data-testid="macro-eyebrow">
      <Aperture className="w-3.5 h-3.5" />
      {m.eyebrow}
    </div>
  ) : null;

  const Title = m.title ? (
    <h2 className="min-w-0 break-words font-serif font-normal leading-[1.1] [overflow-wrap:anywhere]"
        style={{
          fontSize: isMobile ? "clamp(24px, 5.5vw, 28px)" : "clamp(28px, 2.6vw, 36px)",
          color: "#2A1D14",
          letterSpacing: "-0.005em",
        }}
        data-testid="macro-title">
      {m.title}
    </h2>
  ) : null;

  const Subtitle = m.subtitle ? (
    <p className="mt-3 max-w-[50ch] font-serif text-[14px] italic leading-[1.6]"
       style={{ color: "#5B4326" }}
       data-testid="macro-subtitle">
      {m.subtitle}
    </p>
  ) : null;

  const Paragraphs = paragraphs.length ? (
    <div className={`space-y-4 ${isMobile ? "mt-6" : "mt-6"}`}>
      {paragraphs.map((b) => (
        <p key={b.id}
           className="max-w-[58ch] whitespace-pre-wrap font-serif text-[14px] leading-[1.8]"
           style={{ color: "#2A1D14" }}
           data-testid={`macro-paragraph-${b.id}`}>
          {b.text}
        </p>
      ))}
    </div>
  ) : null;

  const Notes = notes.length ? (
    <div className={isMobile ? "mt-6" : "mt-8"}>
      <div className="text-[10px] tracking-[0.28em] uppercase mb-3 pb-2 border-b border-espresso/25"
           style={{ color: "#5B4326" }}>
        Сенсорные ноты
      </div>
      <SensoryList notes={notes} />
    </div>
  ) : null;

  const Accent = (m.accent_phrase && m.accent_phrase.trim()) ? (
    <div className={isMobile ? "mt-6" : "mt-8"}
         data-testid="macro-accent">
      <p className="max-w-[50ch] border-l pl-4 font-serif text-[16px] italic leading-[1.5]"
         style={{ color: "#3E2E20", borderColor: "#A67B4A" }}>
        {m.accent_phrase}
      </p>
    </div>
  ) : null;

  const Caption = (m.caption && m.caption.trim()) ? (
    <div className="mt-3 text-[12px] italic font-serif leading-[1.5] max-w-[42ch]"
         style={{ color: "#5B4326" }}
         data-testid="macro-caption">
      {m.caption}
    </div>
  ) : null;

  return (
    <section className="w-full overflow-hidden"
             style={{ background: "#EFE6D5" }}
             data-testid={`macro-render-${block.id}-${mode}`}>
      <div className={`mx-auto px-7 py-10 sm:px-9 sm:py-12 ${isMobile ? "" : "max-w-[1120px]"}`}>
        {isMobile ? (
          <>
            {Eyebrow}
            {Title}
            {Subtitle}
            {hasImage && (
              <div className="mt-7">
                <MacroImage img={activeImage} mode="mobile" />
                {Caption}
              </div>
            )}
            {Paragraphs}
            {Notes}
            {Accent}
          </>
        ) : hasImage ? (
          <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,56fr)_minmax(0,44fr)] lg:gap-12">
            <div>
              <MacroImage img={activeImage} mode="desktop" />
              {Caption}
            </div>
            <div>
              {Eyebrow}
              {Title}
              {Subtitle}
              {Paragraphs}
              {Notes}
              {Accent}
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-[760px]">
            {Eyebrow}
            {Title}
            {Subtitle}
            {Paragraphs}
            {Notes}
            {Accent}
          </div>
        )}
      </div>
    </section>
  );
}
